import React from 'react';
import { Home, SquareCode, Calculator } from 'lucide-react';

const PropertyInfoSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  propertyTypes 
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {/* Тип имущества */}
      <div>
        <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Home className="w-4 h-4 mr-2 text-indigo-500" />
          Тип имущества
        </label>
        <select
          id="propertyType"
          name="propertyType"
          value={formData.propertyType}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          {propertyTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
        {errors.propertyType && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.propertyType}
          </p>
        )}
      </div>

      {/* Кадастровая стоимость */}
      <div>
        <label htmlFor="cadastralValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Calculator className="w-4 h-4 mr-2 text-indigo-500" />
          Кадастровая стоимость (₽)
        </label>
        <input
          type="text"
          id="cadastralValue"
          name="cadastralValue"
          value={formData.cadastralValue}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.cadastralValue ? 'input-error' : ''}`}
          placeholder="Введите стоимость"
        />
        {errors.cadastralValue && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.cadastralValue}
          </p>
        )}
      </div>

      {/* Площадь */}
      <div>
        <label htmlFor="area" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Площадь (кв.м.)
        </label>
        <input
          type="text"
          id="area"
          name="area"
          value={formData.area}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.area ? 'input-error' : ''}`}
          placeholder="Введите площадь"
        />
        {errors.area && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.area}
          </p>
        )}
      </div>

      {/* Год постройки */}
      <div>
        <label htmlFor="propertyData.constructionYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Год постройки
        </label>
        <input
          type="text"
          id="propertyData.constructionYear"
          name="propertyData.constructionYear"
          value={formData.propertyData.constructionYear}
          onChange={handleInputChange}
          className={`form-input w-full ${errors['propertyData.constructionYear'] ? 'input-error' : ''}`}
          placeholder="Год постройки"
        />
        {errors['propertyData.constructionYear'] && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors['propertyData.constructionYear']}
          </p>
        )}
      </div>

      {/* Налоговый период */}
      <div>
        <label htmlFor="taxYear" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Налоговый период (год)
        </label>
        <input
          type="number"
          id="taxYear"
          name="taxYear"
          value={formData.taxYear}
          onChange={handleInputChange}
          className={`form-input w-full ${errors.taxYear ? 'input-error' : ''}`}
          placeholder="Налоговый период"
          min="2015"
          max={new Date().getFullYear()}
        />
        {errors.taxYear && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.taxYear}
          </p>
        )}
      </div>

      {/* Дата регистрации права собственности */}
      <div>
        <label htmlFor="propertyData.registrationDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Дата регистрации права собственности
        </label>
        <input
          type="date"
          id="propertyData.registrationDate"
          name="propertyData.registrationDate"
          value={formData.propertyData.registrationDate}
          onChange={handleInputChange}
          className={`form-input w-full ${errors['propertyData.registrationDate'] ? 'input-error' : ''}`}
        />
        {errors['propertyData.registrationDate'] && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors['propertyData.registrationDate']}
          </p>
        )}
      </div>

      {/* Дополнительные характеристики объекта */}
      <div className="col-span-full mt-2">
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Дополнительные характеристики
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="propertyData.isHistorical"
                checked={formData.propertyData.isHistorical}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Исторический объект
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="propertyData.isCulturalHeritage"
                checked={formData.propertyData.isCulturalHeritage}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Объект культурного наследия
              </span>
            </label>
          </div>
        </fieldset>
      </div>

      {/* Информационная подсказка */}
      <div className="col-span-full mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start">
          <SquareCode className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Как узнать кадастровую стоимость?
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Кадастровую стоимость можно найти в выписке из ЕГРН, на портале Росреестра (rosreestr.gov.ru) или 
              на портале Госуслуг. Также эта информация содержится в налоговом уведомлении.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyInfoSection;